import React from "react";

import { usePrompts } from "../../../../services/usePrompts";
import CreativeHistoryPanel from "../CreativeHistoryPanel";

/**
 * Workstation · Ads & Creative — **step 7, Prompts, in v2.**
 *
 * WAVE 3, SCREEN 7 — the last of the wizard. Behaviour is `services/usePrompts`,
 * shared with v1.
 *
 * This is the screen whose whole job is to show what the session produced, so a
 * swallowed read here is the worst version of rule 10 in the wizard: an
 * unreachable backend looked exactly like a session that made nothing. The
 * sweep's findings are in the hook.
 */
export default function PromptsStepV2({ offer, sessionId, isNewSession,
                                        sessionStartTime, refreshKey, onFinished }) {
  const P = usePrompts({ offer, sessionId, isNewSession, sessionStartTime,
                         onFinished, refreshKey });

  const auditFor = (productionId) =>
    P.audit.filter((a) => a.production_id === productionId);

  return (
    <div className="ws-step" data-testid="ws-prompts-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Production</h3>
          <p className="dim">
            What this session produced, where it was delivered, and the button
            that closes the session.
          </p>
        </div>
      </div>

      {P.loadState === "loading" ? (
        <div className="state ws-state" data-testid="ws-prompts-loading">
          Reading this session's productions…
        </div>
      ) : null}

      {P.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-prompts-load-failed">
          <p>The productions could not be read: {P.loadError}</p>
          <p className="dim">
            This is a failure, not a session that produced nothing. Nothing has
            been lost.
          </p>
          <button type="button" className="btn-ghost btn-sm" onClick={P.retryLoad}
                  data-testid="ws-prompts-retry">Try again</button>
        </div>
      ) : null}

      {P.loadState === "ok" && P.productions.length === 0 ? (
        <div className="state ws-state" data-testid="ws-prompts-empty">
          This session has not submitted anything to production yet. Scripts are
          submitted on the Creative Elements step.
        </div>
      ) : null}

      {/* "Finished" is a Cipher word, not a production word. Named here because
          the Founder asked where the boundary is. */}
      <p className="dim ws-step-note" data-testid="ws-prompts-meaning">
        Submitted means the brief exists, it is in Drive, and a Trello card is
        open for whoever makes the video. Finishing the session closes Cipher's
        part — the video itself is made by a person from that card.
      </p>

      {/* The last screen in the wizard, and it had no way to re-read itself.
          The delayed re-read above covers the normal case; this covers a slow
          Drive, and it means the operator is never stuck looking at a stale
          answer with no recourse. */}
      {P.loadState === "ok" && P.productions.length > 0 ? (
        <button type="button" className="btn-ghost btn-sm"
                onClick={P.retryLoad}
                data-testid="ws-prompts-refresh">
          Refresh
        </button>
      ) : null}

      {P.scopeNote ? (
        <p className="dim ws-step-note" data-testid="ws-prompts-scope-note">{P.scopeNote}</p>
      ) : null}

      {/* A DELIVERY THAT WAS NEVER CHECKED IS NOT A CLEAN ONE. The old code
          turned every failed audit read into an empty list. */}
      {P.auditFailed > 0 ? (
        <p className="ws-step-note state-warn" role="alert" data-testid="ws-prompts-audit-failed">
          The delivery record could not be read for {P.auditFailed} of{" "}
          {P.productions.length} production(s). Those are shown without one —
          that is a missing check, not a clean result.
        </p>
      ) : null}

      {P.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-prompts-error">
          {P.actionError}
        </p>
      ) : null}

      {P.productions.length > 0 ? (
        <ul className="ws-prod-list">
          {P.productions.map((p) => {
            const entries = auditFor(p.production_id);
            return (
              <li key={p.production_id} className="ws-prod"
                  data-testid={`ws-prod-${p.production_id}`}>
                <div className="ws-prod-head">
                  <span className="ws-script-n">{p.creative_identifier || p.production_id}</span>
                  <span className="dim">{p.creative_type}</span>
                  <span className="ws-chip">{p.status}</span>
                </div>
                {/* WHERE IT ACTUALLY WENT.
                    The Founder, 2026-09-10: "What does submitted actually mean?
                    ...So when is it truly submitted into production?"
                    The record has always answered this — `ai_video_handoff:
                    "HUMAN_VIA_TRELLO"`, `ai_video_submitted_to_model: false`, a
                    Drive URL and a Trello card URL — and the endpoint has always
                    returned it. No screen showed any of it, so "submitted" was a
                    word with no destination attached. */}
                <div className="ws-prod-where" data-testid={`ws-prod-where-${p.production_id}`}>
                  {/* THREE STATES, NOT TWO. On the Founder's own submit this
                      said "not in Drive yet" and "nobody has been handed this
                      yet" while the audit showed DRIVE_UPLOAD SUCCESS and
                      TRELLO_CARD SUCCESS twelve seconds after the record was
                      written. The row is inserted before delivery runs, and
                      this screen read it in between — so it asserted a negative
                      from data read too early, which is worse than a blank. */}
                  {p.google_drive_url ? (
                    <a className="ws-prod-link" href={p.google_drive_url}
                       target="_blank" rel="noreferrer"
                       data-testid={`ws-prod-drive-link-${p.production_id}`}>
                      Brief in Drive
                    </a>
                  ) : P.stillDelivering(p) ? (
                    <span className="dim" data-testid={`ws-prod-drive-pending-${p.production_id}`}>
                      uploading to Drive…
                    </span>
                  ) : (
                    <span className="dim" data-testid={`ws-prod-nodrive-${p.production_id}`}>
                      not in Drive
                    </span>
                  )}
                  {p.trello_card_url ? (
                    <a className="ws-prod-link" href={p.trello_card_url}
                       target="_blank" rel="noreferrer"
                       data-testid={`ws-prod-trello-${p.production_id}`}>
                      Trello card — a person has this
                    </a>
                  ) : P.stillDelivering(p) ? (
                    <span className="dim" data-testid={`ws-prod-trello-pending-${p.production_id}`}>
                      opening the Trello card…
                    </span>
                  ) : (
                    <span className="dim" data-testid={`ws-prod-notrello-${p.production_id}`}>
                      no Trello card — nobody has been handed this yet
                    </span>
                  )}
                  {p.creative_type === "AI" ? (
                    <span className="dim" data-testid={`ws-prod-handoff-${p.production_id}`}>
                      {p.ai_video_submitted_to_model
                        ? "sent to a video model"
                        : "nothing was sent to a video model — a person generates it"}
                    </span>
                  ) : null}
                </div>
                {p.output_url ? (
                  <a className="ws-prod-link" href={p.output_url}
                     target="_blank" rel="noreferrer"
                     data-testid={`ws-prod-url-${p.production_id}`}>Output</a>
                ) : null}
                {p.error ? (
                  <p className="state-error" data-testid={`ws-prod-error-${p.production_id}`}>
                    {p.error}
                  </p>
                ) : null}

                <div className="ws-prod-actions">
                  {/* WAVE 4: the before/after history, reachable FROM the
                      session that made the creative. In v1 it lived only at a
                      route an operator had to already know. */}
                  {p.creative_id ? (
                    <CreativeHistoryPanel creativeId={p.creative_id} />
                  ) : null}
                  <button type="button" className="btn-ghost btn-sm"
                          disabled={P.busy === p.production_id}
                          onClick={() => P.uploadToDrive(p.production_id)}
                          data-testid={`ws-prod-drive-${p.production_id}`}>
                    {P.busy === p.production_id ? "Uploading…" : "Upload to Drive"}
                  </button>
                </div>

                {entries.length > 0 ? (
                  <ul className="ws-prod-audit" data-testid={`ws-prod-audit-${p.production_id}`}>
                    {entries.map((a) => (
                      <li key={a.audit_id}>
                        <span className="dim">{a.status}</span>
                        {a.status && String(a.status).toLowerCase() !== "ok" ? (
                          <button type="button" className="btn-ghost btn-sm"
                                  disabled={P.busy === a.audit_id}
                                  onClick={() => P.retryAudit(a.audit_id)}
                                  data-testid={`ws-prod-audit-retry-${a.audit_id}`}>
                            Retry
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* ── The end of the session ────────────────────────────────────────── */}
      <div className="ws-step-foot">
        <div className="ws-finish">
          {P.finishResult ? (
            <p className="ws-step-note" role="status" data-testid="ws-prompts-finished">
              Session finished.{" "}
              {typeof P.finishResult.scripts_routed === "number"
                ? `${P.finishResult.scripts_routed} script(s) routed.`
                : ""}
            </p>
          ) : (
            <>
              {P.finishError ? (
                <p className="ws-step-note state-error" role="alert"
                   data-testid="ws-prompts-finish-error">{P.finishError}</p>
              ) : null}
              <button type="button" className="btn-primary btn-sm"
                      disabled={P.finishing || !sessionId}
                      onClick={P.finishSession}
                      data-testid="ws-prompts-finish">
                {P.finishing ? "Finishing…" : "Finish session"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
